import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    TopToolbar,
    ExportButton,
    SimpleList,
    useRecordContext
} from 'react-admin';
import { Box, Typography, Grid, useMediaQuery, useTheme, Tooltip, Paper } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Format duration function
const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Custom exporter that excludes id column
const callHistoryExporter = (records) => {
    const csvData = records.map(record => {
        // Create a new object excluding id column
        const filteredRecord = {};
        
        Object.keys(record).forEach(key => {
            // Exclude 'id' column
            if (key !== 'id') {
                if (key === 'duration') {
                    // Format duration for export
                    filteredRecord[key] = formatDuration(record[key]);
                } else if (key === 'callAnswered') {
                    // Format boolean as text for export
                    filteredRecord[key] = record[key] ? 'Yes' : 'No';
                } else {
                    filteredRecord[key] = record[key];
                }
            }
        });

        // Add calculated cost field
        const inboundCost = record.inboundCall?.cost || 0;
        const outboundCost = record.outboundCall?.cost || 0;
        const totalCost = inboundCost + outboundCost;
        filteredRecord.cost = totalCost.toFixed(4);
        
        return filteredRecord;
    });
    
    // Convert to CSV
    const csvContent = convertToCSV(csvData);
    downloadCSV(csvContent, 'call-history');
};

// Helper function to convert data to CSV
const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [
        headers.join(','),
        ...data.map(row => 
            headers.map(header => {
                const value = row[header];
                // Escape commas and quotes in CSV values
                if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                    return `"${value.replace(/"/g, '""')}"`;
                }
                return value || '';
            }).join(',')
        )
    ];
    
    return csvRows.join('\n');
};

// Helper function to download CSV
const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Format date for display
const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';

    try {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    } catch (e) {
        return dateString;
    }
};

// Mobile call history item component
const MobileCallHistoryItem = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Paper elevation={1} sx={{ p: 2, mb: 1.5 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Caller ID</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                        {record.callerId || 'N/A'}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Date/Time</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDateTime(record.startedOn)}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Forwarded to</Typography>
                    {(() => {
                        const forwardedText = record.forwardedTo || 'Not forwarded';
                        
                        if (forwardedText === 'No schedule') {
                            return (
                                <Tooltip title="No appointments (events) were found in the calendar(s) when this call was processed." arrow>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        cursor: 'help',
                                        color: 'warning.main'
                                    }}>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            {forwardedText}
                                        </Typography>
                                        <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 14 }} />
                                    </Box>
                                </Tooltip>
                            );
                        }
                        if (forwardedText === 'Not forwarded') {
                            return (
                                <Tooltip title="Due to a technical issue the call could not be forwarded. The WhoYouGonnaCall team is already notified of this event." arrow>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        cursor: 'help',
                                        color: 'error.main'
                                    }}>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            {forwardedText}
                                        </Typography>
                                        <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 14 }} />
                                    </Box>
                                </Tooltip>
                            );
                        }
                        return (
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                {forwardedText}
                            </Typography>
                        );
                    })()}
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {record.fullname || '-'}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                    }}>
                        <Typography variant="subtitle2" color="text.secondary">Call Answered</Typography>
                        <Box sx={{
                            color: record.callAnswered ? 'green' : 'red',
                            fontWeight: 'bold',
                            fontSize: '1.2em',
                            mt: 0.5
                        }}>
                            {(() => {
                                // Check if there's a phone number in forwardedTo
                                const forwardedText = record.forwardedTo || 'Not forwarded';

                                const hasPhoneNumber = forwardedText &&
                                    forwardedText !== 'No schedule' &&
                                    forwardedText !== 'Not forwarded' &&
                                    forwardedText !== '-' &&
                                    forwardedText !== 'N/A';

                                return hasPhoneNumber ? (record.callAnswered ? '✓' : '✗') : '-';
                            })()}
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                    <Typography variant="body2">
                        {formatDuration(record.duration)}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="subtitle2" color="text.secondary">Cost (€)</Typography>
                    <Typography variant="body2">
                        {(() => {
                            const inboundCost = record.inboundCall?.cost || 0;
                            const outboundCost = record.outboundCall?.cost || 0;
                            const totalCost = inboundCost + outboundCost;
                            return totalCost.toFixed(4);
                        })()}
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

// List actions component with export button
const ListActions = () => (
    <TopToolbar>
        <ExportButton exporter={callHistoryExporter} />
    </TopToolbar>
);

export const CallHistoryList = () => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <List
            perPage={25}
            sort={{ field: 'startedOn', order: 'DESC' }}
            bulkActionButtons={false}
            title="Call History"
            actions={<ListActions />}
        >
            {isSmall ? (
                // Mobile view - SimpleList with custom component
                <SimpleList
                    primaryText={<MobileCallHistoryItem />}
                    sx={{
                        '& .MuiListItem-root': {
                            display: 'block',
                            padding: 0,
                            border: 'none',
                            background: 'transparent',
                            '&:hover': {
                                background: 'transparent'
                            }
                        },
                        '& .MuiListItemText-root': {
                            margin: 0
                        },
                        '& .MuiListItemText-primary': {
                            typography: 'body1'
                        }
                    }}
                />
            ) : (
                // Desktop view - datagrid
                <Datagrid
                    bulkActionButtons={false}
                    sx={{
                        '& [data-field="__selection__"]': { display: 'none' },
                        '& .MuiTableCell-paddingCheckbox': { display: 'none' },
                        '& .MuiTableCell-head': {
                            fontWeight: 'bold',
                        }
                    }}
                >
                    <DateField
                        source="startedOn"
                        label="Date/Time"
                        sortable={false}
                        showTime
                        locales="en-GB"
                        options={{
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                        }}
                    />
                    <TextField source="callerId" label="Caller ID" sortable={false} />
                    <FunctionField
                        label="Forwarded To"
                        sortable={false}
                        render={record => {
                            if (record.forwardedTo === 'No schedule') {
                                return (
                                    <Tooltip title="No appointments (events) were found in the calendar(s) when this call was processed." arrow>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            cursor: 'help',
                                            color: 'warning.main',
                                            '&:hover': {
                                                color: 'warning.dark',
                                            }
                                        }}>
                                            <span>{record.forwardedTo}</span>
                                            <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 16 }} />
                                        </Box>
                                    </Tooltip>
                                );
                            }
                            if (record.forwardedTo === 'Not forwarded') {
                                return (
                                    <Tooltip title="Due to a technical issue the call could not be forwarded. The WhoYouGonnaCall team is already notified of this event." arrow>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            cursor: 'help',
                                            color: 'error.main',
                                            '&:hover': {
                                                color: 'error.dark',
                                            }
                                        }}>
                                            <span>{record.forwardedTo}</span>
                                            <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 16 }} />
                                        </Box>
                                    </Tooltip>
                                );
                            }
                            return record.forwardedTo;
                        }}
                    />
                    <TextField source="fullname" label="Full Name" sortable={false} />
                    <FunctionField
                        label="Call Answered"
                        sortable={false}
                        textAlign="center"
                        render={record => {
                            // Only show icon if forwardedTo contains a phone number (not special messages)
                            const hasPhoneNumber = record.forwardedTo &&
                                record.forwardedTo !== 'No schedule' &&
                                record.forwardedTo !== 'Not forwarded' &&
                                record.forwardedTo !== '-' &&
                                record.forwardedTo !== 'N/A';

                            if (!hasPhoneNumber) {
                                return '-';
                            }

                            return (
                                <Box sx={{
                                    color: record.callAnswered ? 'green' : 'red',
                                    fontWeight: 'bold',
                                    fontSize: '1.2em',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    {record.callAnswered ? '✓' : '✗'}
                                </Box>
                            );
                        }}
                    />
                    <FunctionField
                        label="Duration"
                        sortable={false}
                        render={record => formatDuration(record.duration)}
                    />
                    <FunctionField
                        label="Cost (€)"
                        sortable={false}
                        render={record => {
                            const inboundCost = record.inboundCall?.cost || 0;
                            const outboundCost = record.outboundCall?.cost || 0;
                            const totalCost = inboundCost + outboundCost;
                            return totalCost.toFixed(4);
                        }}
                    />
                </Datagrid>
            )}
        </List>
    );
};